// =============================================================================
// RenewalsScreen — /hr/renewals  (T-449 phase 1)
// Director chase-up cockpit: every employee licence that's EXPIRED or due within
// 90 days, grouped by person, banded expired / ≤30 / ≤60 / ≤90. One click
// "Chase renewal" opens a pre-filled email to the employee listing their items
// (+ renewal link if the licence type has one) AND stamps renewal_requested_at
// so we can see what's already been actioned. Read side is v_compliance_calendar
// territory but we query employee_licences directly for the email + chase state.
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  ArrowLeft, CalendarClock, Mail, AlertTriangle, Download, Search, CheckCircle2,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Button, EmptyState, Spinner, Pill } from "@/components/ui";
import type { Tone } from "@/components/ui";
import { daysToExpiry, fmtDate } from "./licenceHelpers";
import { exportCsv, dated } from "@/lib/exportCsv";

type Band = "expired" | "d30" | "d60" | "d90";

interface Lic {
  licence_id: string;
  employee_id: string;
  expiry_date: string | null;
  renewal_requested_at: string | null;
  type: string;
  category: string | null;
  issuing_body: string | null;
  renewal_url: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  role: string | null;
}
interface Person {
  employee_id: string;
  name: string;
  email: string | null;
  role: string | null;
  worst: Band;
  licences: Lic[];
}

function band(days: number | null): Band {
  if (days === null) return "d90";
  if (days < 0) return "expired";
  if (days <= 30) return "d30";
  if (days <= 60) return "d60";
  return "d90";
}
const BAND_TONE: Record<Band, Tone> = { expired: "overdue", d30: "critical", d60: "warning", d90: "notice" };
const BAND_LABEL: Record<Band, string> = { expired: "Expired", d30: "≤30 days", d60: "≤60 days", d90: "≤90 days" };
const BAND_RANK: Record<Band, number> = { expired: 0, d30: 1, d60: 2, d90: 3 };

function daysText(days: number | null): string {
  if (days === null) return "";
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "due today";
  return `in ${days}d`;
}

export default function RenewalsScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [bandFilter, setBandFilter] = useState<"all" | Band>("all");
  const [notChasedOnly, setNotChasedOnly] = useState(false);

  const horizon = useMemo(() => new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10), []);

  const { data: licences = [], isLoading } = useQuery({
    queryKey: ["renewals", horizon],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_licences")
        .select(`
          licence_id, employee_id, expiry_date, renewal_requested_at,
          licence_types:licence_type_id ( display_name, category, issuing_body, renewal_url ),
          employees:employee_id ( first_name, last_name, email, primary_role_name, is_active )
        `)
        .eq("is_active", true)
        .not("expiry_date", "is", null)
        .lte("expiry_date", horizon)
        .order("expiry_date", { ascending: true })
        .limit(2000);
      if (error) throw error;
      const rows = (data ?? []) as any[];
      const out: Lic[] = [];
      for (const r of rows) {
        const lt = Array.isArray(r.licence_types) ? r.licence_types[0] : r.licence_types;
        const emp = Array.isArray(r.employees) ? r.employees[0] : r.employees;
        if (!emp || emp.is_active === false) continue; // active people only
        out.push({
          licence_id: r.licence_id, employee_id: r.employee_id,
          expiry_date: r.expiry_date, renewal_requested_at: r.renewal_requested_at,
          type: lt?.display_name ?? "Licence", category: lt?.category ?? null,
          issuing_body: lt?.issuing_body ?? null, renewal_url: lt?.renewal_url ?? null,
          first_name: emp?.first_name ?? null, last_name: emp?.last_name ?? null,
          email: emp?.email ?? null, role: emp?.primary_role_name ?? null,
        });
      }
      return out;
    },
  });

  const chase = useMutation({
    mutationFn: async (p: Person) => {
      const ids = p.licences.map((l) => l.licence_id);
      const { error } = await supabase
        .from("employee_licences")
        .update({ renewal_requested_at: new Date().toISOString(), renewal_requested_by: (user as any)?.user_id ?? null })
        .in("licence_id", ids);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["renewals"] }); },
  });

  function openEmail(p: Person) {
    const first = p.name.split(" ")[0] || "there";
    const lines = p.licences
      .slice()
      .sort((a, b) => (daysToExpiry(a.expiry_date) ?? 0) - (daysToExpiry(b.expiry_date) ?? 0))
      .map((l) => {
        const d = daysToExpiry(l.expiry_date);
        const when = d !== null && d < 0 ? `EXPIRED ${fmtDate(l.expiry_date)}` : `expires ${fmtDate(l.expiry_date)} (${daysText(d)})`;
        const link = l.renewal_url ? `\n    Renew: ${l.renewal_url}` : "";
        return `  • ${l.type} — ${when}${link}`;
      })
      .join("\n");
    const subject = `Coretel — ticket/licence renewal due${p.licences.length > 1 ? ` (${p.licences.length} items)` : ""}`;
    const body =
      `Hi ${first},\n\n` +
      `The following need renewing so you stay site-ready:\n\n${lines}\n\n` +
      `Please get these sorted as soon as you can and send the updated ticket through to the office so we can verify it.\n\n` +
      `Thanks,\nCoretel Group`;
    window.location.href = `mailto:${p.email ?? ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  async function chaseAndEmail(p: Person) {
    try { await chase.mutateAsync(p); } catch { /* still let them email */ }
    openEmail(p);
  }

  const people = useMemo<Person[]>(() => {
    const needle = q.trim().toLowerCase();
    const m = new Map<string, Person>();
    for (const l of licences) {
      const d = daysToExpiry(l.expiry_date);
      const b = band(d);
      if (bandFilter !== "all" && b !== bandFilter) continue;
      if (notChasedOnly && l.renewal_requested_at) continue;
      const name = [l.first_name, l.last_name].filter(Boolean).join(" ") || "Unnamed";
      if (needle && !(`${name} ${l.type}`.toLowerCase().includes(needle))) continue;
      let p = m.get(l.employee_id);
      if (!p) { p = { employee_id: l.employee_id, name, email: l.email, role: l.role, worst: b, licences: [] }; m.set(l.employee_id, p); }
      p.licences.push(l);
      if (BAND_RANK[b] < BAND_RANK[p.worst]) p.worst = b;
    }
    return Array.from(m.values()).sort((a, b) => BAND_RANK[a.worst] - BAND_RANK[b.worst] || a.name.localeCompare(b.name));
  }, [licences, q, bandFilter, notChasedOnly]);

  const kpi = useMemo(() => {
    let expired = 0, d30 = 0, d60 = 0, d90 = 0;
    const ppl = new Set<string>();
    for (const l of licences) {
      const b = band(daysToExpiry(l.expiry_date));
      if (b === "expired") expired++; else if (b === "d30") d30++; else if (b === "d60") d60++; else d90++;
      if (b === "expired" || b === "d30") ppl.add(l.employee_id);
    }
    return { expired, d30, d60, d90, chaseNow: ppl.size };
  }, [licences]);

  function doExport() {
    const flat = people.flatMap((p) =>
      p.licences.map((l) => ({ p, l })),
    );
    exportCsv(dated("renewals_due"), flat, [
      { header: "Employee", value: (x) => x.p.name },
      { header: "Role", value: (x) => x.p.role ?? "" },
      { header: "Licence", value: (x) => x.l.type },
      { header: "Expiry", value: (x) => (x.l.expiry_date ? fmtDate(x.l.expiry_date) : "") },
      { header: "Days", value: (x) => daysToExpiry(x.l.expiry_date) ?? "" },
      { header: "Band", value: (x) => BAND_LABEL[band(daysToExpiry(x.l.expiry_date))] },
      { header: "Chased", value: (x) => (x.l.renewal_requested_at ? fmtDate(x.l.renewal_requested_at) : "") },
    ]);
  }

  if (!user) return null;
  if (!hasRole(user, "manager")) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><EmptyState icon={AlertTriangle} title="Managers and directors only" description="Renewals is for the people who manage compliance." /></Card>
      </div>
    );
  }

  const BANDS: Band[] = ["expired", "d30", "d60", "d90"];

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto pb-16">
      <Link to="/hr" className="text-text-muted text-sm flex items-center gap-1 hover:text-text mb-2">
        <ArrowLeft size={14} /> Back to HR
      </Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <CalendarClock size={22} className="text-accent" /> Renewals
          </h1>
          <div className="text-text-muted text-[13px] mt-1">
            Tickets expired or due within 90 days, by person. Chase one and it emails them + logs the date.
          </div>
        </div>
        <Button variant="secondary" icon={Download} onClick={doExport} disabled={!people.length}>Export CSV</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <KPI label="Expired"        value={String(kpi.expired)} accent={kpi.expired > 0} />
        <KPI label="Due ≤30 days"   value={String(kpi.d30)} accent={kpi.d30 > 0} />
        <KPI label="Due ≤60 days"   value={String(kpi.d60)} />
        <KPI label="Due ≤90 days"   value={String(kpi.d90)} />
        <KPI label="People to chase" value={String(kpi.chaseNow)} sub="expired or ≤30d" />
      </div>

      <Card className="mb-4">
        <div className="p-3 md:p-4 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search person or ticket…"
              className="w-full bg-ink border border-line rounded pl-8 pr-2.5 py-1.5 text-[13px] text-text" />
          </div>
          <div className="inline-flex border border-line rounded-md bg-ink-raised p-0.5 gap-0.5">
            <button onClick={() => setBandFilter("all")} className={`px-2.5 py-1 rounded text-[12px] ${bandFilter === "all" ? "bg-accent text-white" : "text-text-muted hover:text-text"}`}>All</button>
            {BANDS.map((b) => (
              <button key={b} onClick={() => setBandFilter(b)} className={`px-2.5 py-1 rounded text-[12px] ${bandFilter === b ? "bg-accent text-white" : "text-text-muted hover:text-text"}`}>{BAND_LABEL[b]}</button>
            ))}
          </div>
          <label className="inline-flex items-center gap-1.5 cursor-pointer text-[12px] text-text-muted">
            <input type="checkbox" className="accent-accent" checked={notChasedOnly} onChange={(e) => setNotChasedOnly(e.target.checked)} />
            Not yet chased
          </label>
        </div>
      </Card>

      {isLoading ? (
        <div className="py-16 flex justify-center"><Spinner size={22} /></div>
      ) : !people.length ? (
        <Card><EmptyState icon={CheckCircle2} title="Nothing due" description="No tickets expired or due within 90 days for that filter. Nice." /></Card>
      ) : (
        <div className="space-y-3">
          {people.map((p) => {
            const chased = p.licences.every((l) => l.renewal_requested_at);
            const lastChase = p.licences.map((l) => l.renewal_requested_at).filter(Boolean).sort().slice(-1)[0];
            return (
              <Card key={p.employee_id}>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <Link to={`/people/${p.employee_id}`} className="font-semibold text-text hover:text-accent">{p.name}</Link>
                      <span className="text-text-dim text-[12px] ml-2">{p.role ?? ""}</span>
                      {!p.email && <span className="text-danger text-[11px] ml-2">· no email on file</span>}
                      {chased && lastChase && <span className="text-ok text-[11px] ml-2">· chased {fmtDate(lastChase)}</span>}
                    </div>
                    <Button size="sm" icon={Mail} disabled={!p.email || chase.isPending}
                      onClick={() => chaseAndEmail(p)}>
                      {chased ? "Chase again" : "Chase renewal"}
                    </Button>
                  </div>
                  <div className="mt-3 divide-y divide-line/60">
                    {p.licences
                      .slice()
                      .sort((a, b) => (daysToExpiry(a.expiry_date) ?? 0) - (daysToExpiry(b.expiry_date) ?? 0))
                      .map((l) => {
                        const d = daysToExpiry(l.expiry_date);
                        const b = band(d);
                        return (
                          <div key={l.licence_id} className="flex items-center gap-3 py-1.5 text-[13px]">
                            <Pill label={BAND_LABEL[b]} tone={BAND_TONE[b]} small />
                            <span className="text-text font-medium">{l.type}</span>
                            {l.category && <span className="text-text-dim text-[11px]">{l.category}</span>}
                            <span className="ml-auto font-mono text-text-muted">{fmtDate(l.expiry_date)}</span>
                            <span className={`font-mono text-[12px] w-24 text-right ${d !== null && d < 0 ? "text-danger" : d !== null && d <= 30 ? "text-warn" : "text-text-dim"}`}>{daysText(d)}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
